import React, { useEffect, useReducer } from 'react';
import styled from '@emotion/styled';
import { COLOR_GREEN } from 'client/components/MarketingPagesShared/styles';
import { useT } from 'client/i18n/hooks/useT';
import { PODCAST_AD_TYPES } from './constants';
import { SCREEN_BREAKPOINTS } from '../../constants';
import {
  CarouselNav,
  CarouselNavButton,
  CarouselSlide,
  CarouselStats,
  CarouselType,
  TriangleIcon,
} from './components';
import { initialState, reducer } from './store';
import {
  ROTATE_SLIDE_INDEX_ARRAY,
  CLICK_EVENT,
  TOUCH_START_EVENT,
  TOUCH_MOVE_EVENT,
  TOUCH_END_EVENT,
} from './types';

const CarouselSection = styled.section`
  background: ${COLOR_GREEN};
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 10px 10% 100px;
  overflow: hidden;
  @media (max-width: ${SCREEN_BREAKPOINTS.TABLET}px) {
    padding: 65px 5%;
  }
`;

const CarouselTitle = styled.h2`
  margin: 0 0 100px;
  color: #ffffff;
  font-weight: 900;
  font-size: 6rem;
  text-align: center;
  @media (max-width: 600px) {
    margin: 0 0 50px;
    font-size: 2.8rem;
  }
`;

const CarouselSlidesContainer = styled.div`
  position: relative;
  margin: 50px 0;
  transition: height 0.75s ease-in-out;
`;

export const Carousel = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    activeAdIndex,
    activeSlide,
    isRotating,
    nextActiveAdIndex,
    rotatingAdIndex,
    slideIndexArray,
  } = state;
  const t = useT();

  useEffect(() => {
    if (activeAdIndex !== nextActiveAdIndex) {
      setTimeout(
        () => dispatch({ type: ROTATE_SLIDE_INDEX_ARRAY }),
        isRotating ? 750 : 0
      );
    }
  }, [activeAdIndex, isRotating, nextActiveAdIndex]);

  return (
    <CarouselSection>
      <CarouselContainer>
        <CarouselTitle>
          {t(
            'AdsByAnchorScreen.Carousel.Title',
            'Ways to earn with Anchor',
            'Ads by anchor page, ads features carousel title'
          )}
        </CarouselTitle>
        <CarouselNav
          activeAdIndex={activeAdIndex}
          setNextActiveAdIndex={dispatch}
        />
        <CarouselType adType={activeSlide.adType} />
        <CarouselSlidesContainer role="region" aria-roledescription="carousel">
          <CarouselNavButton
            aria-label={t(
              'AdsByAnchorScreen.Carousel.PrevButtonAriaLabel',
              'Previous Slide. {currentSlideIndex} of {lastSlideIndex}',
              'Ads by anchor page, ads features carousel previous slide button screenreader information',
              {
                currentSlideIndex: activeAdIndex || slideIndexArray.length,
                lastSlideIndex: slideIndexArray.length,
              }
            )}
            data-type="prev"
            onClick={() => {
              dispatch({
                type: CLICK_EVENT,
                value:
                  slideIndexArray[slideIndexArray.indexOf(activeAdIndex) - 1],
              });
            }}
            onTouchEnd={() => {
              dispatch({ type: TOUCH_END_EVENT, value: -1 });
            }}
            onTouchMove={event => {
              dispatch({
                type: TOUCH_MOVE_EVENT,
                value: event.targetTouches[0].clientX,
              });
            }}
            onTouchStart={event => {
              dispatch({
                type: TOUCH_START_EVENT,
                value: event.targetTouches[0].clientX,
              });
            }}
          >
            <TriangleIcon />
          </CarouselNavButton>
          {PODCAST_AD_TYPES.map((ad, index) => {
            const title = ad.getTitle(t);
            return (
              <CarouselSlide
                activeAdIndex={activeAdIndex}
                ad={ad}
                index={index}
                key={`carousel-slide__${title}`}
                rotatingAdIndex={rotatingAdIndex}
                slideIndexArray={slideIndexArray}
              />
            );
          })}
          <CarouselNavButton
            aria-label={t(
              'AdsByAnchorScreen.Carousel.NextButtonAriaLabel',
              'Next Slide. {currentSlideIndex} of {lastSlideIndex}',
              'Ads by anchor page, ads features carousel next slide button screenreader information',
              {
                currentSlideIndex:
                  activeAdIndex + 1 !== slideIndexArray.length
                    ? activeAdIndex + 2
                    : 1,
                lastSlideIndex: slideIndexArray.length,
              }
            )}
            data-type="next"
            onClick={() => {
              dispatch({
                type: CLICK_EVENT,
                value:
                  slideIndexArray[slideIndexArray.indexOf(activeAdIndex) + 1],
              });
            }}
            onTouchEnd={() => {
              dispatch({ type: TOUCH_END_EVENT, value: 1 });
            }}
            onTouchMove={event => {
              dispatch({
                type: TOUCH_MOVE_EVENT,
                value: event.targetTouches[0].clientX,
              });
            }}
            onTouchStart={event => {
              dispatch({
                type: TOUCH_START_EVENT,
                value: event.targetTouches[0].clientX,
              });
            }}
          >
            <TriangleIcon />
          </CarouselNavButton>
        </CarouselSlidesContainer>
        <CarouselStats stats={activeSlide.stats} />
      </CarouselContainer>
    </CarouselSection>
  );
};
